/**
 * agentArtifacts — persist AI-generated artifacts to `agent_artifacts`.
 *
 * All public functions return null on failure rather than throwing — callers
 * should treat them as fire-and-forget (void / .catch(() => {})).
 *
 * Body is capped at 12 000 chars before insert to guard against runaway sizes.
 */

import { supabase } from '../../lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ArtifactKind =
  | 'cover_letter'
  | 'application_letter'
  | 'company_research'
  | 'profile_assessment'
  | 'optimization_summary';

export interface SaveArtifactInput {
  sessionId?: string | null;
  kind:        ArtifactKind;
  title:       string;
  body:        string;
  metadata?:   Record<string, unknown>;
  jobId?:      string | null;
}

export interface AgentArtifact {
  id:         string;
  user_id:    string;
  session_id: string | null;
  kind:       ArtifactKind;
  title:      string;
  body:       string;
  metadata:   Record<string, unknown>;
  job_id:     string | null;
  created_at: string;
}

const BODY_MAX = 12_000;

// ── saveArtifact ──────────────────────────────────────────────────────────────

/**
 * Insert one artifact row for the current user.
 * Returns the new row's id on success, or null on any failure.
 */
export async function saveArtifact(
  input: SaveArtifactInput,
): Promise<{ id: string } | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return null;

    const { data, error } = await supabase
      .from('agent_artifacts')
      .insert({
        user_id:    userId,
        session_id: input.sessionId ?? null,
        kind:       input.kind,
        title:      input.title.slice(0, 500),
        body:       input.body.slice(0, BODY_MAX),
        metadata:   input.metadata ?? {},
        job_id:     input.jobId ?? null,
      })
      .select('id')
      .single();

    if (error) {
      console.warn('[agentArtifacts] saveArtifact failed:', error.message);
      return null;
    }

    return { id: (data as { id: string }).id };
  } catch (err) {
    console.warn('[agentArtifacts] saveArtifact exception:', err);
    return null;
  }
}

// ── listArtifacts ─────────────────────────────────────────────────────────────

/**
 * Fetch the current user's artifacts, optionally filtered by kind.
 * Returns an empty array on failure.
 */
export async function listArtifacts(
  kind?: ArtifactKind,
  limit = 50,
): Promise<AgentArtifact[]> {
  try {
    let query = supabase
      .from('agent_artifacts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (kind) {
      query = query.eq('kind', kind);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('[agentArtifacts] listArtifacts failed:', error.message);
      return [];
    }

    return (data ?? []) as AgentArtifact[];
  } catch (err) {
    console.warn('[agentArtifacts] listArtifacts exception:', err);
    return [];
  }
}
